
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { calculateLearningStreak } from "@/utils/streakUtils";

/**
 * Hook to fetch comprehensive user progress data including:
 * - Completed lessons, modules, and topics
 * - Current learning streak
 * - Daily/weekly XP totals
 * - Last activity information
 */
export const useUserProgress = () => {
  return useQuery({
    queryKey: ['userProgress'],
    queryFn: async () => {
      console.log("🎯 Fetching comprehensive user progress data");
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log("❌ No authenticated user found");
        return null;
      }

      // Fetch user progress data
      const { data: userProgressData, error: progressError } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id);

      if (progressError) {
        console.error("❌ Error fetching user progress:", progressError);
        throw progressError;
      }

      // Fetch user points data
      const { data: userPointsData, error: pointsError } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (pointsError && pointsError.code !== 'PGRST116') {
        console.error("❌ Error fetching user points:", pointsError);
        throw pointsError;
      }

      // Calculate completed items
      const completedLessons = userProgressData?.filter(p => p.status === 'completed') || [];
      const completedTopics = new Set(completedLessons.map(p => p.lesson_id)).size;
      
      // Calculate current learning streak
      const currentStreak = calculateLearningStreak(userProgressData || []);
      
      // Calculate today's XP (points earned today)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayXP = completedLessons
        .filter(lesson => {
          const completedDate = new Date(lesson.completed_at);
          completedDate.setHours(0, 0, 0, 0);
          return completedDate.getTime() === today.getTime();
        })
        .reduce((total, lesson) => total + (lesson.points || 0), 0);

      const progressSummary = {
        // Completed counts
        completedLessons: completedLessons.length,
        completedTopics: completedTopics,
        
        // Points and streaks
        totalXP: userPointsData?.total_points || 0,
        weeklyXP: userPointsData?.weekly_points || 0,
        todayXP: todayXP,
        currentStreak: currentStreak,
        
        // Activity data
        lastActivity: completedLessons.length > 0 
          ? new Date(Math.max(...completedLessons.map(l => new Date(l.completed_at).getTime())))
          : null,
        
        // Raw data for further processing
        userProgress: userProgressData || [],
        userPoints: userPointsData
      };

      console.log("📊 User progress summary:", progressSummary);
      return progressSummary;
    },
    retry: 2,
    refetchOnWindowFocus: false
  });
};
